<h2 mat-dialog-title class="aca-admin-dialog__title">
  <mat-icon>{{ isEdit ? 'edit' : 'add_task' }}</mat-icon>
  {{ (isEdit ? 'DMS.JOBS.EDIT_JOB' : 'DMS.JOBS.NEW_JOB') | translate }}
</h2>

<div mat-dialog-content class="aca-admin-dialog__body aca-jobdlg__body">
  <!-- ── general ─────────────────────────────────────────────────────────── -->
  <mat-form-field appearance="outline" class="aca-admin-dialog__full">
    <mat-label>{{ 'DMS.JOBS.NAME' | translate }}</mat-label>
    <input matInput [(ngModel)]="model.name" [placeholder]="'DMS.JOBS.NAME_HINT' | translate" />
  </mat-form-field>
  <mat-slide-toggle [(ngModel)]="model.enabled" color="primary">{{ 'DMS.JOBS.ENABLED' | translate }}</mat-slide-toggle>
  <div class="aca-jobdlg__dryrun">
    <mat-slide-toggle [(ngModel)]="model.dryRun" color="primary">{{ 'DMS.JOBS.DRYRUN' | translate }}</mat-slide-toggle>
    <p class="aca-jobdlg__hint">{{ 'DMS.JOBS.DRYRUN_HINT' | translate }}</p>
  </div>

  <!-- ── conditions ──────────────────────────────────────────────────────── -->
  <h3 class="aca-jobdlg__section">{{ 'DMS.JOBS.CONDITIONS' | translate }}</h3>
  <p class="aca-jobdlg__hint">{{ 'DMS.JOBS.CONDITIONS_HINT' | translate }}</p>

  <div class="aca-jobdlg__rule" *ngFor="let rule of model.condition.rules; let i = index">
    <mat-form-field appearance="outline" class="aca-jobdlg__rulefield">
      <mat-label>{{ 'DMS.JOBS.FIELD' | translate }}</mat-label>
      <mat-select [(ngModel)]="rule.field" (ngModelChange)="onRuleChange(rule)">
        <mat-option *ngFor="let f of fieldOptions" [value]="f.field">{{ f.label | translate }}</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="aca-jobdlg__ruleop">
      <mat-label>{{ 'DMS.JOBS.OPERATOR' | translate }}</mat-label>
      <mat-select [(ngModel)]="rule.op" (ngModelChange)="preview.set(null)">
        <mat-option *ngFor="let op of opsFor(rule)" [value]="op">{{ opLabel(op) }}</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="aca-jobdlg__rulevalue" *ngIf="needsValue(rule)">
      <mat-label>{{ 'DMS.JOBS.VALUE' | translate }}</mat-label>
      <mat-select *ngIf="fieldOption(rule.field)?.options?.length; else plainValue" [(ngModel)]="rule.value" (ngModelChange)="preview.set(null)">
        <mat-option *ngFor="let o of fieldOption(rule.field)?.options" [value]="o.value">{{ o.label | translate }}</mat-option>
      </mat-select>
      <ng-template #plainValue>
        <input
          *ngIf="isDateValue(rule); else textValue"
          matInput
          [matDatepicker]="valuePicker"
          [placeholder]="'DMS.DATE_INPUT.PLACEHOLDER' | translate"
          [ngModel]="rule.value | acaIsoDate"
          (ngModelChange)="rule.value = toIsoDate($event); preview.set(null)"
        />
      </ng-template>
      <ng-template #textValue>
        <input
          matInput
          [type]="fieldOption(rule.field)?.numeric ? 'number' : 'text'"
          [(ngModel)]="rule.value"
          (ngModelChange)="preview.set(null); resolveFolderNames()"
          [placeholder]="fieldOption(rule.field)?.dateLike && ['olderThanDays', 'newerThanDays'].includes(rule.op) ? '365' : ''"
        />
        <button
          *ngIf="rule.field === 'ANCESTOR'"
          type="button"
          mat-icon-button
          matSuffix
          (click)="browseFolder(rule)"
          [matTooltip]="'DMS.JOBS.BROWSE_FOLDER' | translate"
          [attr.aria-label]="'DMS.JOBS.BROWSE_FOLDER' | translate"
        >
          <mat-icon>folder_open</mat-icon>
        </button>
        <mat-hint *ngIf="rule.field === 'ANCESTOR' && rule.value">{{ folderLabel(rule.value) }}</mat-hint>
      </ng-template>
      <!-- direct children, so the form field puts the toggle in its suffix slot -->
      <mat-datepicker-toggle *ngIf="isDateValue(rule)" matIconSuffix [for]="valuePicker" />
      <mat-datepicker #valuePicker />
    </mat-form-field>

    <mat-form-field appearance="outline" class="aca-jobdlg__rulevalue" *ngIf="rule.op === 'between'">
      <mat-label>{{ 'DMS.JOBS.VALUE_TO' | translate }}</mat-label>
      <input
        *ngIf="fieldOption(rule.field)?.numeric; else valueToDate"
        matInput
        type="number"
        [(ngModel)]="rule.valueTo"
        (ngModelChange)="preview.set(null)"
      />
      <ng-template #valueToDate>
        <input
          matInput
          [matDatepicker]="valueToPicker"
          [placeholder]="'DMS.DATE_INPUT.PLACEHOLDER' | translate"
          [ngModel]="rule.valueTo | acaIsoDate"
          (ngModelChange)="rule.valueTo = toIsoDate($event); preview.set(null)"
        />
      </ng-template>
      <mat-datepicker-toggle *ngIf="!fieldOption(rule.field)?.numeric" matIconSuffix [for]="valueToPicker" />
      <mat-datepicker #valueToPicker />
    </mat-form-field>

    <button
      mat-icon-button
      class="aca-jobdlg__ruledel"
      (click)="removeRule(i)"
      [matTooltip]="'DMS.JOBS.REMOVE_RULE' | translate"
      [attr.aria-label]="'DMS.JOBS.REMOVE_RULE' | translate"
    >
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <button type="button" class="aca-jobdlg__addrule" (click)="addRule()"><mat-icon>add</mat-icon>{{ 'DMS.JOBS.ADD_RULE' | translate }}</button>

  <mat-form-field appearance="outline" class="aca-admin-dialog__full">
    <mat-label>{{ 'DMS.JOBS.EXTRA_QUERY' | translate }}</mat-label>
    <input matInput [(ngModel)]="model.condition.extraQuery" placeholder="AFTS (optional)" (ngModelChange)="preview.set(null)" />
    <mat-hint>{{ 'DMS.JOBS.EXTRA_QUERY_HINT' | translate }}</mat-hint>
  </mat-form-field>

  <!-- ── action ──────────────────────────────────────────────────────────── -->
  <h3 class="aca-jobdlg__section">{{ 'DMS.JOBS.ACTION' | translate }}</h3>

  <mat-form-field appearance="outline" class="aca-admin-dialog__full">
    <mat-label>{{ 'DMS.JOBS.ACTION_TYPE' | translate }}</mat-label>
    <mat-select [(ngModel)]="model.action.type">
      <mat-option *ngFor="let a of actions" [value]="a">{{ 'DMS.JOBS.ACT_' + a.toUpperCase() | translate }}</mat-option>
    </mat-select>
  </mat-form-field>

  <mat-form-field *ngIf="model.action.type === 'moveTo'" appearance="outline" class="aca-admin-dialog__full">
    <mat-label>{{ 'DMS.JOBS.ACTION_PATH' | translate }}</mat-label>
    <input matInput [(ngModel)]="model.action.path" placeholder="เช่น Archive/2026" />
    <button
      type="button"
      mat-icon-button
      matSuffix
      (click)="browseActionPath()"
      [matTooltip]="'DMS.JOBS.BROWSE_FOLDER' | translate"
      [attr.aria-label]="'DMS.JOBS.BROWSE_FOLDER' | translate"
    >
      <mat-icon>folder_open</mat-icon>
    </button>
  </mat-form-field>

  <ng-container *ngIf="model.action.type === 'setExpiry'">
    <div class="aca-admin-dialog__row">
      <mat-form-field appearance="outline">
        <mat-label>{{ 'DMS.JOBS.ACTION_DAYS' | translate }}</mat-label>
        <input matInput type="number" [(ngModel)]="model.action.days" [disabled]="!!model.action.date" />
      </mat-form-field>
      <mat-form-field appearance="outline">
        <mat-label>{{ 'DMS.JOBS.ACTION_DATE' | translate }}</mat-label>
        <input
          matInput
          [matDatepicker]="actionDatePicker"
          [placeholder]="'DMS.DATE_INPUT.PLACEHOLDER' | translate"
          [ngModel]="model.action.date | acaIsoDate"
          (ngModelChange)="model.action.date = toIsoDate($event)"
        />
        <mat-datepicker-toggle matIconSuffix [for]="actionDatePicker" />
        <mat-datepicker #actionDatePicker />
      </mat-form-field>
    </div>
    <p class="aca-jobdlg__hint">{{ 'DMS.JOBS.ACTION_DATE_HINT' | translate }}</p>
  </ng-container>

  <mat-form-field *ngIf="model.action.type === 'addTag' || model.action.type === 'notify'" appearance="outline" class="aca-admin-dialog__full">
    <mat-label>{{ model.action.type === 'addTag' ? ('DMS.JOBS.ACTION_TAG' | translate) : ('DMS.JOBS.ACTION_MARKTAG' | translate) }}</mat-label>
    <input matInput [(ngModel)]="model.action.tag" [placeholder]="model.action.type === 'notify' ? 'notified-invoice' : 'needs-review'" />
    <mat-hint *ngIf="model.action.type === 'notify'">{{ 'DMS.JOBS.ACTION_MARKTAG_HINT' | translate }}</mat-hint>
  </mat-form-field>

  <mat-checkbox *ngIf="model.action.type === 'delete'" [(ngModel)]="model.action.permanent" color="warn">
    {{ 'DMS.JOBS.ACTION_PERMANENT' | translate }}
  </mat-checkbox>

  <ng-container *ngIf="model.action.type === 'notify'">
    <mat-form-field appearance="outline" class="aca-admin-dialog__full">
      <mat-label>{{ 'DMS.JOBS.ACTION_TEMPLATE' | translate }}</mat-label>
      <input matInput [(ngModel)]="model.action.template" placeholder="expiry-warning" />
      <mat-hint>{{ 'DMS.JOBS.ACTION_TEMPLATE_HINT' | translate }}</mat-hint>
    </mat-form-field>
    <mat-form-field appearance="outline" class="aca-admin-dialog__full">
      <mat-label>{{ 'DMS.JOBS.ACTION_RECIPIENTS' | translate }}</mat-label>
      <input matInput [(ngModel)]="actionRecipients" placeholder="owner, group:GROUP_DMS_ADMIN, email:a@b.co" />
      <mat-hint>{{ 'DMS.JOBS.RECIPIENTS_HINT' | translate }}</mat-hint>
    </mat-form-field>
  </ng-container>

  <mat-form-field *ngIf="model.action.type === 'script'" appearance="outline" class="aca-admin-dialog__full">
    <mat-label>{{ 'DMS.JOBS.ACTION_SCRIPT' | translate }}</mat-label>
    <input matInput [(ngModel)]="model.action.script" placeholder="Data Dictionary/Scripts/my-script.js" />
    <mat-hint>{{ 'DMS.JOBS.ACTION_SCRIPT_HINT' | translate }}</mat-hint>
  </mat-form-field>

  <!-- ── schedule ────────────────────────────────────────────────────────── -->
  <h3 class="aca-jobdlg__section">{{ 'DMS.JOBS.SCHEDULE' | translate }}</h3>

  <div class="aca-admin-dialog__row">
    <mat-form-field appearance="outline">
      <mat-label>{{ 'DMS.JOBS.SCHEDULE_PRESET' | translate }}</mat-label>
      <mat-select [(ngModel)]="schedulePreset" (ngModelChange)="onPresetChange()">
        <mat-option *ngFor="let p of presets" [value]="p.key">{{ p.labelKey | translate }}</mat-option>
      </mat-select>
    </mat-form-field>
    <mat-form-field appearance="outline">
      <mat-label>{{ 'DMS.JOBS.TIMEZONE' | translate }}</mat-label>
      <input matInput [(ngModel)]="model.schedule.timezone" />
    </mat-form-field>
  </div>

  <mat-form-field *ngIf="schedulePreset === 'custom'" appearance="outline" class="aca-admin-dialog__full">
    <mat-label>cron</mat-label>
    <input matInput [(ngModel)]="model.schedule.cron" placeholder="0 0 2 * * *" />
    <mat-hint>{{ 'DMS.JOBS.CRON_HINT' | translate }}</mat-hint>
  </mat-form-field>

  <!-- ── completion notification ─────────────────────────────────────────── -->
  <h3 class="aca-jobdlg__section">{{ 'DMS.JOBS.NOTIFY' | translate }}</h3>

  <mat-slide-toggle [(ngModel)]="model.notify.onComplete" color="primary">{{ 'DMS.JOBS.NOTIFY_ONCOMPLETE' | translate }}</mat-slide-toggle>

  <ng-container *ngIf="model.notify.onComplete">
    <mat-checkbox [(ngModel)]="model.notify.onlyWhenMatched">{{ 'DMS.JOBS.NOTIFY_ONLYMATCHED' | translate }}</mat-checkbox>
    <mat-form-field appearance="outline" class="aca-admin-dialog__full">
      <mat-label>{{ 'DMS.JOBS.NOTIFY_RECIPIENTS' | translate }}</mat-label>
      <input matInput [(ngModel)]="notifyRecipients" placeholder="group:GROUP_DMS_ADMIN, email:admin@b.co" />
      <mat-hint>{{ 'DMS.JOBS.RECIPIENTS_HINT' | translate }}</mat-hint>
    </mat-form-field>
    <div class="aca-admin-dialog__row">
      <mat-form-field appearance="outline">
        <mat-label>{{ 'DMS.JOBS.NOTIFY_TEMPLATE' | translate }}</mat-label>
        <input matInput [(ngModel)]="model.notify.template" placeholder="job-report" />
      </mat-form-field>
      <mat-checkbox [(ngModel)]="model.notify.attachList" class="aca-jobdlg__attach">{{ 'DMS.JOBS.NOTIFY_ATTACHLIST' | translate }}</mat-checkbox>
    </div>
  </ng-container>

  <!-- ── preview result ──────────────────────────────────────────────────── -->
  <div class="aca-jobdlg__preview" *ngIf="preview() as p">
    <div class="aca-jobdlg__previewhead">
      <mat-icon>visibility</mat-icon>
      <strong>{{ 'DMS.JOBS.PREVIEW_RESULT' | translate: { matched: p.matched } }}</strong>
    </div>
    <code class="aca-jobdlg__query">{{ p.query }}</code>
    <ul class="aca-jobdlg__sample" *ngIf="p.sample?.length">
      <li *ngFor="let s of p.sample">
        {{ s.name }} <span class="aca-jobdlg__samplepath">{{ s.path }}</span>
      </li>
    </ul>
  </div>

  <p class="aca-admin-dialog__error" *ngIf="error">{{ error }}</p>
</div>

<div mat-dialog-actions align="end" class="aca-admin-dialog__actions">
  <button mat-button (click)="runPreview()" [disabled]="previewing || saving">
    <mat-icon>visibility</mat-icon>
    {{ previewing ? ('DMS.JOBS.PREVIEWING' | translate) : ('DMS.JOBS.PREVIEW' | translate) }}
  </button>
  <span class="aca-jobdlg__spacer"></span>
  <button mat-button (click)="close()">{{ 'DMS.JOBS.CANCEL' | translate }}</button>
  <button mat-flat-button color="primary" (click)="save()" [disabled]="saving || previewing || !valid()">
    {{ saving ? ('DMS.JOBS.SAVING' | translate) : ('DMS.JOBS.SAVE' | translate) }}
  </button>
</div>
